import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Breadcrumbs from '../../components/Breadcrumbs';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Sun, Euro, Leaf, CheckCircle2, ArrowRight, Phone, MapPin, Award } from 'lucide-react';

// Datos de todas las ciudades
const ciudadesData = {
  // Comunidad de Madrid
  madrid: {
    nombre: 'Madrid',
    poblacion: '3.300.000',
    descripcion: 'la capital de España y centro neurálgico de la Comunidad de Madrid',
    caracteristicas: 'Con su clima mediterráneo continentalizado y más de 2.800 horas de sol al año',
    zonas: ['Centro', 'Salamanca', 'Chamartín', 'Chamberí', 'Retiro', 'Arganzuela']
  },
  'alcala-de-henares': {
    nombre: 'Alcalá de Henares',
    poblacion: '195.000',
    descripcion: 'una ciudad Patrimonio de la Humanidad ubicada al este de la Comunidad de Madrid',
    caracteristicas: 'Con excelente radiación solar y amplio desarrollo de viviendas unifamiliares',
    zonas: ['Centro Histórico', 'Ensanche', 'Espartales', 'Nueva Alcalá']
  },
  getafe: {
    nombre: 'Getafe',
    poblacion: '183.000',
    descripcion: 'una ciudad industrial y residencial al sur de Madrid',
    caracteristicas: 'Con gran potencial para instalaciones solares tanto residenciales como industriales',
    zonas: ['Centro', 'Juan de la Cierva', 'Sector III', 'Buenavista']
  },
  leganes: {
    nombre: 'Leganés',
    poblacion: '190.000',
    descripcion: 'un importante municipio del sur metropolitano de Madrid',
    caracteristicas: 'Con amplias zonas residenciales ideales para autoconsumo solar',
    zonas: ['Centro', 'Arroyo Culebro', 'Vereda de los Estudiantes']
  },
  alcorcon: {
    nombre: 'Alcorcón',
    poblacion: '170.000',
    descripcion: 'una ciudad dinámica en la zona suroeste de Madrid',
    caracteristicas: 'Con óptimas condiciones climáticas para energía fotovoltaica',
    zonas: ['Centro', 'Parque Lisboa', 'Parque Oeste', 'Los Castillos']
  },
  mostoles: {
    nombre: 'Móstoles',
    poblacion: '209.000',
    descripcion: 'uno de los municipios más poblados de la Comunidad de Madrid',
    caracteristicas: 'Con gran demanda de soluciones de energía renovable',
    zonas: ['Centro', 'Parque Coimbra', 'Villafontana', 'El Soto']
  },
  fuenlabrada: {
    nombre: 'Fuenlabrada',
    poblacion: '193.000',
    descripcion: 'una ciudad en plena expansión al sur de Madrid',
    caracteristicas: 'Con excelente radiación solar y alto interés en sostenibilidad',
    zonas: ['Centro', 'Loranca', 'El Vivero', 'La Fuente']
  },
  parla: {
    nombre: 'Parla',
    poblacion: '130.000',
    descripcion: 'un municipio estratégico en el sur de la Comunidad de Madrid',
    caracteristicas: 'Con condiciones climáticas favorables para instalaciones solares',
    zonas: ['Centro', 'Parla Este', 'Parla Oeste', 'San Blas']
  },
  'pozuelo-de-alarcon': {
    nombre: 'Pozuelo de Alarcón',
    poblacion: '86.000',
    descripcion: 'uno de los municipios con mayor renta per cápita de España',
    caracteristicas: 'Con abundantes viviendas unifamiliares y chalets ideales para energía solar',
    zonas: ['Centro', 'Somosaguas', 'La Finca', 'Ciudad de la Imagen']
  },
  majadahonda: {
    nombre: 'Majadahonda',
    poblacion: '71.000',
    descripcion: 'una ciudad residencial de alto standing al noroeste de Madrid',
    caracteristicas: 'Con gran demanda de instalaciones solares premium',
    zonas: ['Centro', 'Monte Rozas', 'Atalayuela']
  },
  // Comunidad Valenciana
  valencia: {
    nombre: 'Valencia',
    poblacion: '800.000',
    descripcion: 'la capital de la Comunidad Valenciana y tercera ciudad de España',
    caracteristicas: 'Con más de 300 días de sol al año y clima mediterráneo ideal',
    zonas: ['Centro', 'Russafa', 'Campanar', 'Benimaclet', 'Patraix']
  },
  alicante: {
    nombre: 'Alicante',
    poblacion: '337.000',
    descripcion: 'una ciudad costera con excepcional radiación solar',
    caracteristicas: 'Con más de 2.900 horas de sol anuales, perfecta para energía fotovoltaica',
    zonas: ['Centro', 'San Blas', 'Carolinas', 'Vistahermosa']
  },
  castellon: {
    nombre: 'Castellón',
    poblacion: '174.000',
    descripcion: 'la capital de la provincia más septentrional de la Comunidad Valenciana',
    caracteristicas: 'Con excelentes condiciones climáticas para instalaciones solares',
    zonas: ['Centro', 'Grao', 'Rafalafena', 'San Lorenzo']
  },
  torrent: {
    nombre: 'Torrent',
    poblacion: '83.000',
    descripcion: 'una ciudad del área metropolitana de Valencia',
    caracteristicas: 'Con alto potencial para energía solar residencial',
    zonas: ['Centro', 'Parc Central', 'Xenillet', 'El Vedat']
  },
  gandia: {
    nombre: 'Gandía',
    poblacion: '75.000',
    descripcion: 'una ciudad costera de la provincia de Valencia',
    caracteristicas: 'Con clima mediterráneo y alta insolación anual',
    zonas: ['Centro', 'Playa de Gandía', 'Rafalcaid']
  },
  elche: {
    nombre: 'Elche',
    poblacion: '234.000',
    descripcion: 'la segunda ciudad más poblada de la provincia de Alicante',
    caracteristicas: 'Con más de 300 días de sol al año, ideal para autoconsumo',
    zonas: ['Centro', 'Altabix', 'Carrús', 'El Pla']
  },
  benidorm: {
    nombre: 'Benidorm',
    poblacion: '70.000',
    descripcion: 'el principal destino turístico de la Costa Blanca',
    caracteristicas: 'Con clima privilegiado y máxima radiación solar',
    zonas: ['Centro', 'Levante', 'Poniente', 'La Cala']
  },
  sagunto: {
    nombre: 'Sagunto',
    poblacion: '66.000',
    descripcion: 'una ciudad histórica de la provincia de Valencia',
    caracteristicas: 'Con excelentes condiciones para instalaciones fotovoltaicas',
    zonas: ['Centro', 'Puerto de Sagunto', 'Almardà']
  },
  paterna: {
    nombre: 'Paterna',
    poblacion: '70.000',
    descripcion: 'un municipio industrial del área metropolitana de Valencia',
    caracteristicas: 'Con gran potencial para energía solar empresarial y residencial',
    zonas: ['Centro', 'Campamento', 'Terramelar']
  },
  burjassot: {
    nombre: 'Burjassot',
    poblacion: '38.000',
    descripcion: 'una ciudad universitaria cercana a Valencia',
    caracteristicas: 'Con alto interés en sostenibilidad y energías renovables',
    zonas: ['Centro', 'Bulevar Sur', 'Ciudad Jardín']
  },
  // Extremadura
  badajoz: {
    nombre: 'Badajoz',
    poblacion: '151.000',
    descripcion: 'la capital de la provincia más extensa de España',
    caracteristicas: 'Con más de 3.000 horas de sol anuales, récord nacional',
    zonas: ['Centro', 'San Roque', 'Cerro de Reyes', 'La Paz']
  },
  caceres: {
    nombre: 'Cáceres',
    poblacion: '96.000',
    descripcion: 'una ciudad Patrimonio de la Humanidad',
    caracteristicas: 'Con excepcional radiación solar y clima continental',
    zonas: ['Centro Histórico', 'Aldea Moret', 'Mejostilla']
  },
  merida: {
    nombre: 'Mérida',
    poblacion: '60.000',
    descripcion: 'la capital de Extremadura y antigua ciudad romana',
    caracteristicas: 'Con más de 2.900 horas de sol al año',
    zonas: ['Centro', 'Nueva Ciudad', 'El Prado', 'Las Abadías']
  },
  plasencia: {
    nombre: 'Plasencia',
    poblacion: '41.000',
    descripcion: 'puerta del Valle del Jerte',
    caracteristicas: 'Con excelente radiación solar y clima mediterráneo continentalizado',
    zonas: ['Centro', 'La Coronación', 'La Paz', 'Los Pinos']
  },
  almendralejo: {
    nombre: 'Almendralejo',
    poblacion: '35.000',
    descripcion: 'la capital de Tierra de Barros',
    caracteristicas: 'Con condiciones climáticas óptimas para energía fotovoltaica',
    zonas: ['Centro', 'Santiago', 'San José']
  },
  'don-benito': {
    nombre: 'Don Benito',
    poblacion: '37.000',
    descripcion: 'un importante centro agrícola de Vegas Altas',
    caracteristicas: 'Con alta insolación y gran potencial solar',
    zonas: ['Centro', 'Barrio de la Estación', 'Barriada del Pilar']
  },
  'villanueva-de-la-serena': {
    nombre: 'Villanueva de la Serena',
    poblacion: '26.000',
    descripcion: 'una ciudad de Vegas Altas del Guadiana',
    caracteristicas: 'Con más de 3.000 horas de sol anuales',
    zonas: ['Centro', 'Barrio Zurbaran', 'Barriada Hernán Cortés']
  },
  'navalmoral-de-la-mata': {
    nombre: 'Navalmoral de la Mata',
    poblacion: '17.000',
    descripcion: 'puerta del Campo Arañuelo',
    caracteristicas: 'Con excelente radiación solar y clima mediterráneo',
    zonas: ['Centro', 'Zona Norte', 'Zona Sur']
  }
};

const PlacasSolaresCiudad = () => {
  const { ciudad } = useParams();
  
  // Buscar datos de la ciudad
  const data = ciudadesData[ciudad];
  
  // Si no existe la ciudad, mostrar mensaje
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-4xl font-bold text-nubia-green-primary mb-4">
            Ciudad no encontrada
          </h1>
          <p className="text-lg text-nubia-text-dark/70 mb-6">
            La ciudad "{ciudad}" no está disponible en nuestro sistema.
          </p>
          <Link to="/">
            <Button className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary">
              Volver al Inicio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Meta tags dinámicos
  const pageTitle = `Placas Solares ${data.nombre} - Instalación y Ahorro | Nubia Solar`;
  const metaDescription = `Instalación de placas solares en ${data.nombre}. Ahorra hasta 70% en tu factura. Subvenciones y ayudas locales. ☎️ 604 92 17 45`;
  const metaKeywords = `placas solares ${data.nombre.toLowerCase()}, instalacion energia solar ${data.nombre.toLowerCase()}, paneles solares ${data.nombre.toLowerCase()}`;

  const breadcrumbItems = [
    { label: 'Inicio', href: '/' },
    { label: 'Ciudades', href: '/ciudades' },
    { label: data.nombre, href: '' }
  ];

  const beneficios = [
    'Ahorro de hasta 70% en tu factura eléctrica',
    'Amortización de la inversión en 1-3 años',
    'Incremento del valor de tu vivienda hasta 15.000€',
    'Reducción de emisiones de CO₂',
    'Independencia energética frente a subidas de luz',
    'Mantenimiento mínimo durante más de 25 años'
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={`https://nubiasolar.es/placas-solares/${ciudad}`} />
      </Helmet>

      <Breadcrumbs items={breadcrumbItems} />

      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-24 bg-nubia-gray-light">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <MapPin className="w-8 h-8 text-nubia-gold-primary" />
                <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary">
                  Placas Solares en {data.nombre}
                </h1>
              </div>
              <p className="text-2xl text-nubia-text-dark/80 mb-8">
                Instalación profesional de paneles solares en {data.nombre} con hasta 70% de ahorro en tu factura
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contacto">
                  <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-6 shadow-xl">
                    Solicitar Presupuesto Gratis
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-nubia-green-primary text-nubia-green-primary hover:bg-nubia-green-primary hover:text-white px-10 py-6"
                  onClick={() => window.location.href = 'tel:+34604921745'}
                >
                  <Phone className="mr-2 w-5 h-5" />
                  604 92 17 45
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 text-center">
              <Card className="nubia-shadow border-0">
                <CardContent className="pt-8 pb-6">
                  <Award className="w-12 h-12 text-nubia-gold-primary mx-auto mb-4" />
                  <div className="text-4xl font-bold text-nubia-green-primary mb-2">1000+</div>
                  <div className="text-nubia-text-dark/70">Instalaciones en {data.nombre}</div>
                </CardContent>
              </Card>
              <Card className="nubia-shadow border-0">
                <CardContent className="pt-8 pb-6">
                  <Sun className="w-12 h-12 text-nubia-gold-primary mx-auto mb-4" />
                  <div className="text-4xl font-bold text-nubia-green-primary mb-2">1-2 días</div>
                  <div className="text-nubia-text-dark/70">Instalación completa</div>
                </CardContent>
              </Card>
              <Card className="nubia-shadow border-0">
                <CardContent className="pt-8 pb-6">
                  <CheckCircle2 className="w-12 h-12 text-nubia-gold-primary mx-auto mb-4" />
                  <div className="text-4xl font-bold text-nubia-green-primary mb-2">25 años</div>
                  <div className="text-nubia-text-dark/70">Garantía total</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Sobre la ciudad */}
        <section className="py-20 bg-nubia-gray-light">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-8 text-center">
                Energía Solar en {data.nombre}
              </h2>
              <div className="bg-white rounded-2xl p-8 nubia-shadow mb-8">
                <p className="text-lg text-nubia-text-dark/80 leading-relaxed mb-6">
                  {data.nombre} es {data.descripcion}. {data.caracteristicas}, 
                  es una ubicación ideal para la instalación de placas solares y el aprovechamiento 
                  de la energía fotovoltaica.
                </p>
                <p className="text-lg text-nubia-text-dark/80 leading-relaxed">
                  En <strong>Nubia Solar</strong> somos especialistas en instalaciones de paneles solares 
                  en {data.nombre}. Contamos con más de 1.000 instalaciones exitosas y un equipo de 
                  profesionales certificados que garantizan la máxima calidad y eficiencia.
                </p>
              </div>

              {/* Zonas de servicio */}
              <Card className="nubia-shadow border-0">
                <CardHeader>
                  <CardTitle className="text-2xl text-nubia-green-primary">
                    Zonas de {data.nombre} donde instalamos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-nubia-text-dark/70 mb-4">
                    Prestamos servicio en todas las zonas de {data.nombre}, incluyendo:
                  </p>
                  <div className="text-nubia-green-support font-medium">
                    {data.zonas.join(' • ')}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Beneficios */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-12 text-center">
                Beneficios de Instalar Placas Solares
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {beneficios.map((beneficio, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-nubia-green-support mt-1 flex-shrink-0" />
                    <span className="text-lg text-nubia-text-dark/80">{beneficio}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Por qué elegirnos */}
        <section className="py-20 nubia-gradient text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                ¿Por Qué Elegir Nubia Solar en {data.nombre}?
              </h2>
              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <div>
                  <Award className="w-16 h-16 text-nubia-gold-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Experiencia Local</h3>
                  <p className="text-white/90">
                    Conocemos perfectamente las características climáticas y normativas de {data.nombre}
                  </p>
                </div>
                <div>
                  <Euro className="w-16 h-16 text-nubia-gold-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Mejor Precio</h3>
                  <p className="text-white/90">
                    Precios competitivos y tramitación gratuita de todas las ayudas disponibles
                  </p>
                </div>
                <div>
                  <Leaf className="w-16 h-16 text-nubia-gold-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Compromiso Verde</h3>
                  <p className="text-white/90">
                    Contribuye al futuro sostenible de {data.nombre} con energía 100% renovable
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-nubia-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
              Empieza a Ahorrar Hoy en {data.nombre}
            </h2>
            <p className="text-xl text-nubia-text-dark/70 mb-10 max-w-3xl mx-auto">
              Solicita tu estudio solar gratuito y descubre cuánto puedes ahorrar con placas solares en {data.nombre}
            </p>
            <Link to="/contacto">
              <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-12 py-7 text-lg shadow-xl">
                Solicitar Presupuesto Gratis
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default PlacasSolaresCiudad;
